import { deserialize, serialize } from 'flatgeobuf/lib/mjs/geojson'
import type { Feature, FeatureCollection } from 'geojson'

type GenericProperties = Record<string, unknown>

type ManagedSchemaKey =
  | 'hidden'
  | 'showLabel'
  | 'labelMinZoom'
  | 'stroke'
  | 'fill'
  | 'marker-color'
  | 'stroke-opacity'
  | 'fill-opacity'
  | 'line-style'

// Default value (and therefore column type) for every managed style property.
// FlatGeobuf stores properties in a shared columnar schema derived from the
// first feature, so seeding these keys guarantees the renderer/editor fields
// always exist as typed columns even when the source KML omitted them.
export const MANAGED_SCHEMA_DEFAULTS: Record<ManagedSchemaKey, boolean | number | string> = {
  hidden: false,
  showLabel: false,
  labelMinZoom: 6,
  stroke: '',
  fill: '',
  'marker-color': '',
  'stroke-opacity': 0.9,
  'fill-opacity': 0.2,
  'line-style': 'solid'
}

// Number of deserialized FeatureCollections kept in the read cache.
export const FLATGEOBUF_READ_CACHE_MAX_ENTRIES = 16

type SchemaColumnKind = 'bool' | 'number' | 'string' | 'json' | 'binary'

interface SchemaColumn {
  kind: SchemaColumnKind
  fallback: unknown
}

// Maps a runtime value to the FlatGeobuf column kind it would be encoded as.
// Returns null for absent values (null/undefined) and unsupported types.
const resolveValueKind = (value: unknown): SchemaColumnKind | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return 'bool'
  if (typeof value === 'number') return Number.isFinite(value) ? 'number' : null
  if (typeof value === 'string') return 'string'
  if (value instanceof Uint8Array) return 'binary'
  if (typeof value === 'object') return 'json'
  return null
}

const fallbackForKind = (kind: SchemaColumnKind): unknown => {
  switch (kind) {
    case 'bool':
      return false
    case 'number':
      return 0
    case 'string':
      return ''
    case 'json':
      return {}
    case 'binary':
      return new Uint8Array(0)
    default:
      return ''
  }
}

// Coerces a source value to the resolved column kind, falling back to the
// column's typed default whenever the value is missing or incompatible. This
// keeps the columnar schema homogeneous so serialization never writes
// `undefined` or a value whose type mismatches the column.
const coerceValueToColumn = (value: unknown, column: SchemaColumn): unknown => {
  if (value === null || value === undefined) {
    return column.fallback
  }

  switch (column.kind) {
    case 'bool':
      return typeof value === 'boolean' ? value : column.fallback
    case 'number': {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : column.fallback
      }
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : column.fallback
    }
    case 'string':
      if (typeof value === 'string') return value
      if (typeof value === 'number' || typeof value === 'boolean') return String(value)
      return column.fallback
    case 'json':
      // Any JSON-serializable value round-trips through a Json column.
      return value
    case 'binary':
      return value instanceof Uint8Array ? value : column.fallback
    default:
      return column.fallback
  }
}

// Builds a unified columnar schema and rewrites every feature so all features
// share the exact same property keys, each with a consistent type and never
// null/undefined. This is required because FlatGeobuf breaks on heterogeneous
// schemas (the header is inferred from the first feature only).
export const prepareFeatureCollectionForFlatGeobufSchema = (collection: FeatureCollection): FeatureCollection => {
  const features = Array.isArray(collection?.features) ? collection.features : []

  const schema = new Map<string, SchemaColumn>()

  // 1. Seed managed style keys with their fixed kinds + defaults.
  ;(Object.keys(MANAGED_SCHEMA_DEFAULTS) as ManagedSchemaKey[]).forEach((key) => {
    const defaultValue = MANAGED_SCHEMA_DEFAULTS[key]
    schema.set(key, { kind: resolveValueKind(defaultValue) as SchemaColumnKind, fallback: defaultValue })
  })

  // 2. Discover remaining keys, inferring each column kind from the first
  //    non-null value observed across all features.
  features.forEach((feature) => {
    const properties = (feature?.properties ?? null) as GenericProperties | null
    if (!properties) return

    Object.keys(properties).forEach((key) => {
      if (schema.has(key)) return

      const kind = resolveValueKind(properties[key])
      if (kind === null) return

      schema.set(key, { kind, fallback: fallbackForKind(kind) })
    })
  })

  // 3. Keys that only ever appeared as null/undefined default to string.
  features.forEach((feature) => {
    const properties = (feature?.properties ?? null) as GenericProperties | null
    if (!properties) return

    Object.keys(properties).forEach((key) => {
      if (!schema.has(key)) {
        schema.set(key, { kind: 'string', fallback: '' })
      }
    })
  })

  // 4. Rebuild each feature's properties to exactly match the schema.
  const normalizedFeatures: Feature[] = features.map((feature) => {
    const sourceProperties = (feature?.properties ?? null) as GenericProperties | null
    const properties: GenericProperties = {}

    schema.forEach((column, key) => {
      properties[key] = coerceValueToColumn(sourceProperties ? sourceProperties[key] : undefined, column)
    })

    return { ...feature, properties }
  })

  return {
    ...collection,
    type: 'FeatureCollection',
    features: normalizedFeatures
  }
}

// Serializes a FeatureCollection to FlatGeobuf bytes after unifying its schema.
export const serializeFeatureCollectionToFlatGeobuf = (collection: FeatureCollection): Uint8Array => {
  const prepared = prepareFeatureCollectionForFlatGeobufSchema(collection)

  if (prepared.features.length === 0) {
    throw new Error('Cannot serialize an empty FeatureCollection to FlatGeobuf')
  }

  return serialize(prepared)
}

// Deserializes FlatGeobuf bytes back into a FeatureCollection, re-promoting the
// original `properties.id` to `feature.id` (FlatGeobuf assigns a sequential
// integer id on read, discarding the original).
export const deserializeFlatGeobufBytes = async (bytes: Uint8Array): Promise<FeatureCollection> => {
  const features: Feature[] = []

  for await (const rawFeature of deserialize(bytes)) {
    const feature = rawFeature as Feature
    const promotedId = (feature.properties as GenericProperties | null)?.id

    if (promotedId !== undefined && promotedId !== null) {
      feature.id = promotedId as string | number
    }

    features.push(feature)
  }

  return {
    type: 'FeatureCollection',
    features
  }
}

const buildReadCacheKey = (id: string, updatedAt: string | number | Date): string => {
  const stamp = updatedAt instanceof Date ? updatedAt.toISOString() : String(updatedAt)
  return `${id}:${stamp}`
}

const readCache = new Map<string, FeatureCollection>()

export interface ReadCachedFlatGeobufParams {
  id: string
  updatedAt: string | number | Date
  // Invoked only on a cache miss to obtain the raw .fgb bytes.
  readBytes: () => Promise<Uint8Array> | Uint8Array
}

// Deserializes a geometry's .fgb with an in-memory LRU cache keyed by
// `id:updatedAt`, so unchanged geometries are parsed at most once.
export const readCachedFlatGeobufFeatureCollection = async (
  params: ReadCachedFlatGeobufParams
): Promise<FeatureCollection> => {
  const key = buildReadCacheKey(params.id, params.updatedAt)

  if (readCache.has(key)) {
    const cached = readCache.get(key) as FeatureCollection
    // Mark as most-recently-used.
    readCache.delete(key)
    readCache.set(key, cached)
    return cached
  }

  const bytes = await params.readBytes()
  const collection = await deserializeFlatGeobufBytes(bytes)

  readCache.set(key, collection)
  if (readCache.size > FLATGEOBUF_READ_CACHE_MAX_ENTRIES) {
    const oldestKey = readCache.keys().next().value
    if (oldestKey !== undefined) {
      readCache.delete(oldestKey)
    }
  }

  return collection
}

export const clearFlatGeobufReadCache = (): void => {
  readCache.clear()
}
